import { getTargetPeriod } from '../../utils/periodUtils';
import type { QueueDocument } from './useDashboardData';

type GroupableDocument = Pick<
  QueueDocument,
  | 'document_id'
  | 'user_id'
  | 'user_full_name'
  | 'user_email'
  | 'organization_name'
  | 'document_name'
  | 'document_type'
  | 'applicable_month'
  | 'applicable_year'
  | 'due_date'
  | 'upload_date'
  | 'renewal_type'
  | 'renewal_interval'
>;

export interface PendingGroup<T extends GroupableDocument = GroupableDocument> {
  key: string;
  /** Oldest pending period in the group; the row links here. */
  oldest: T;
  documents: T[];
  documentName: string;
  count: number;
  /** "feb 2025", "ene–dic 2025" or "nov 2024–feb 2025". */
  rangeLabel: string;
}

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

export function pendingPeriod(doc: GroupableDocument) {
  return getTargetPeriod({
    applicableMonth: doc.applicable_month,
    applicableYear: doc.applicable_year,
    dueDate: doc.due_date,
    uploadDate: doc.upload_date,
    renewalType: doc.renewal_type,
    renewalInterval: doc.renewal_interval as string | null,
  });
}

const periodIndex = ({ month, year }: { month: number; year: number }) => year * 12 + (month - 1);

export function formatPeriodRange(
  first: { month: number; year: number },
  last: { month: number; year: number },
) {
  const label = (p: { month: number; year: number }) => `${MONTHS[p.month - 1] ?? '?'} ${p.year}`;
  if (periodIndex(first) === periodIndex(last)) return label(first);
  if (first.year === last.year) return `${MONTHS[first.month - 1] ?? '?'}–${label(last)}`;
  return `${label(first)}–${label(last)}`;
}

/**
 * Groups pending documents by (user, document definition) so a supplier who
 * uploaded a whole year of the same requirement shows as one row. Groups keep
 * the order in which they first appear in the queue.
 */
export function groupPendingDocuments<T extends GroupableDocument>(documents: T[]): PendingGroup<T>[] {
  const groups = new Map<string, T[]>();
  documents.forEach((doc) => {
    const definition = doc.document_type || doc.document_name || '';
    const key = `${doc.user_id}::${definition}`;
    const bucket = groups.get(key);
    if (bucket) bucket.push(doc);
    else groups.set(key, [doc]);
  });
  return Array.from(groups.entries()).map(([key, docs]) => {
    const withPeriod = docs
      .map((doc) => ({ doc, period: pendingPeriod(doc) }))
      .sort(
        (a, b) =>
          periodIndex(a.period) - periodIndex(b.period) ||
          String(a.doc.upload_date || '').localeCompare(String(b.doc.upload_date || '')),
      );
    const first = withPeriod[0];
    const last = withPeriod[withPeriod.length - 1];
    return {
      key,
      oldest: first.doc,
      documents: withPeriod.map((item) => item.doc),
      documentName: first.doc.document_type || first.doc.document_name,
      count: docs.length,
      rangeLabel: formatPeriodRange(first.period, last.period),
    };
  });
}
